import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Button, Col, Input, Popconfirm, Popover, Row, Select, TreeSelect, notification } from 'antd'
import dayjs from 'dayjs'
import { useOutletContext, useParams } from 'react-router-dom'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  getHtmlParentElement,
  handleModalTriggerEvent,
  renderTranslation,
  sendMessage,
  stopPropagation,
} from '../../../Helpers/GenericHelpers'
import { DeleteFilled, CopyOutlined, SaveFilled, CloseOutlined } from '@ant-design/icons'
import {
  deleteRule,
  generateLabels,
  saveSeasonRule,
  saveSeasonRuleCopy,
} from '../../../Services/Omnichannel/SeasonRuleService'
import { DateFormat, listingsAllValue, maxRuleLength, RuleType, TimeFormat } from '../../../Constants/Configuration'
import {
  setCurrentRuleSelected,
  setHasRuleSelectedChangesToSave,
  toggleSeasonConfirmChangeModal,
} from '../../../Slices/Omnichannel/SeasonRuleSlice'
import {
  isRuleEditToDisable,
  isRuleNameMaxLengthValid,
  isRuleNameMinLengthValid,
  isRuleNameUnique,
  retrieveRule,
  retrieveCurrentSeasonAndRules,
  generateRuleCopy,
  isRuleLockedByOtherUsers,
  isRuleLockedByCurrentUser,
  retrieveActiveListings,
} from '../../../Helpers/DiscountHelper'
import {
  getHoursBeforeCurrentHour,
  getMinutesBeforeCurrentMinutesForCurrentHour,
  isDateBefore,
  isSameDateTime,
} from '../../../Helpers/DateHelpers'
import {
  TypeOfChannel,
  resetPriceListsToCopyTo,
  setActivePriceListToBeCopy,
} from '../../../Slices/Omnichannel/DiscountSlice'
import { getDiscountData } from '../../../Services/Omnichannel/DiscountService'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { useNavigateWrapper } from '../../../Helpers/NavigateHelper'
import ConfirmSeasonRuleChangeModal from 'Components/Omnichannel/ConfirmSeasonRuleChangeModal'
import DiscountTable from 'Components/Omnichannel/DiscountTable'
import { TypeOfLock, acquireLock } from '../../../Services/Omnichannel/LockService'
import { customEqual as lockEquality } from 'Helpers/LockHelper'
import DateTimeRangeFilter from 'Components/Generic/DateTimeFilter'
import { getAnswers } from 'Services/Settings/SettingsService'
import { QuestionCode, SettingsName } from 'Constants/Settings'
import ExcelButton from 'Components/Generic/MenuExcel'
import LabelsIcon from 'Components/Svg/LabelsIcon'

function RuleDetail() {
  const dispatch = useAppDispatch()
  const navigationWrapper = useNavigateWrapper()
  const language = useAppSelector(state => state.present.language.value)
  const seasonRuleList = useAppSelector(state => state.present.seasonRule.seasonRuleList)
  const discountToSave = useAppSelector(state => state.present.discount.discountToSave)
  const hasRuleSelectedChangesToSave = useAppSelector(state => state.present.seasonRule.hasRuleSelectedChangesToSave)
  const cleaningPopup = useAppSelector(state => state.present.discount.cleaningPopup)
  const locks = useAppSelector(state => state.present.locks.locks, lockEquality)
  const user = useAppSelector(state => state.present.configuration.userValue)
  const answers = useAppSelector(state => state.present.settings.answers)
  const typeOfChannel: TypeOfChannel = useAppSelector(state => state.present.discount.typeOfChannel)
  const allPricelists = useAppSelector(state => state.present.configuration.allPricelists)

  const currentActiveListings = useMemo(() => {
    return retrieveActiveListings(typeOfChannel, allPricelists)
  }, [typeOfChannel, allPricelists])

  const listingDefaultValue = useMemo(() => {
    return { label: renderTranslation(language, TranslationKeys.channelListOptionAllText), value: listingsAllValue }
  }, [language])

  const [ruleName, setRuleName] = useState('')
  const [seasonName, setSeasonName] = useState('')
  const [ruleType, setRuleType] = useState(RuleType.season)
  const [startDateTime, setStartDateTime] = useState(dayjs.tz(new Date(), 'Europe/Rome'))
  const [endDateTime, setEndDateTime] = useState(dayjs.tz(new Date(), 'Europe/Rome'))
  const [currentListing, setListings] = useState([listingDefaultValue])
  const [decimalPriceList, setDecimalPriceList] = useState([])
  const [ruleDecimalPrice, setRuleDecimalPrice] = useState('0')
  const [isGeneratingLabels, setIsGeneratingLabels] = useState(false)

  const listingDropdownRef = useRef(null)
  const ruleTypeDropdownRef = useRef(null)
  const ruleDecimalDropdownRef = useRef(null)

  const bodyrefContext = useOutletContext() as React.MutableRefObject<undefined>
  const { id } = useParams()
  const ruleId = parseInt(id)
  const isTypeOfChannelStore = typeOfChannel === TypeOfChannel.Store

  const initMapRuleData = useCallback(
    currentRule => {
      if (currentRule) {
        setRuleName(currentRule.name)
        setRuleType(currentRule.ruleType)
        setSeasonName(currentRule.season.name)
        setStartDateTime(dayjs(currentRule.startDate))
        setEndDateTime(dayjs(currentRule.endDate))
        setListings([listingDefaultValue])
        setRuleDecimalPrice(currentRule.ruleDecimalPrice ? currentRule.ruleDecimalPrice : '0')
      } else {
        setRuleName('')
        setRuleType(RuleType.season)
        setStartDateTime(dayjs())
        setEndDateTime(dayjs())
        setListings([listingDefaultValue])
        setRuleDecimalPrice('0')
      }
    },
    [listingDefaultValue],
  )

  const [currentRule, currentSeasonRules] = useMemo(() => {
    const seasonMatch = retrieveCurrentSeasonAndRules(ruleId, seasonRuleList)
    const ruleMatch = retrieveRule(ruleId, seasonRuleList)
    const seasonRules = seasonMatch?.rules ?? []

    if (ruleMatch) {
      initMapRuleData(ruleMatch)
      return [ruleMatch, seasonRules]
    }

    initMapRuleData(undefined)

    return [null, []]
  }, [ruleId, seasonRuleList, initMapRuleData])

  useEffect(() => {
    if (ruleId) {
      dispatch(setCurrentRuleSelected(ruleId))
    } else {
      dispatch(setCurrentRuleSelected(undefined))
      setRuleDecimalPrice('0')
    }
  }, [dispatch, ruleId])

  const isRuleVirtual = currentRule?.virtualId
  const isAlwaysOnSaleRule = currentRule?.season?.alwaysOnSale
  const ruleOptionsType = useMemo(() => {
    const season = { label: renderTranslation(language, TranslationKeys.ruleBoxInSeason), value: RuleType.season }
    const salesPeriod = {
      label: renderTranslation(language, TranslationKeys.ruleBoxSalesPeriod),
      value: RuleType.salesPeriod,
    }
    const promo = { label: renderTranslation(language, TranslationKeys.ruleBoxPromo), value: RuleType.promo }

    if (isTypeOfChannelStore) {
      //when view condition is store view, rule can be created only on sale or on promo
      return [salesPeriod, promo]
    }

    return [season, salesPeriod, promo]
  }, [isTypeOfChannelStore, language])

  const noDataMessage = (
    <>
      <div>{renderTranslation(language, TranslationKeys.warningNoData)}</div>
      <ul>
        <li>{renderTranslation(language, TranslationKeys.checkArticlesInSeason)}</li>
        <li> {renderTranslation(language, TranslationKeys.checkArticlesSuperGroup)}</li>
        <li> {renderTranslation(language, TranslationKeys.checkArticlesBrand)}</li>
        <li>{renderTranslation(language, TranslationKeys.checkPricelistChannelAssociation)}</li>
      </ul>
    </>
  )
  useEffect(() => {
    if (Object.keys(currentRule ?? {}).length > 0) {
      const seasonMatch = retrieveCurrentSeasonAndRules(currentRule.id, seasonRuleList)
      dispatch(setActivePriceListToBeCopy(undefined))
      dispatch(resetPriceListsToCopyTo())
      dispatch(
        getDiscountData(isRuleVirtual ? currentRule.virtualId : currentRule.id, seasonMatch?.season, noDataMessage),
      )
    }
  }, [currentRule, dispatch, isRuleVirtual, seasonRuleList, typeOfChannel])

  useEffect(() => {
    let interval = 0
    if (hasRuleSelectedChangesToSave) {
      interval = window.setInterval(() => {
        dispatch(acquireLock(true))
      }, 15000)
    }
    return () => {
      if (interval !== 0) {
        clearInterval(interval)
      }
    }
  }, [dispatch, hasRuleSelectedChangesToSave])

  const editDisabled = useMemo(() => {
    const result =
      isRuleEditToDisable(currentRule, currentRule?.isActive) ||
      isRuleLockedByOtherUsers(locks, currentRule?.id, TypeOfLock.Omnichannel, user)
    return result
  }, [currentRule, locks, user])

  const isStartDateUnique = useMemo(() => {
    const checkedResultByRuleType = currentSeasonRules.filter(x => x.id !== currentRule.id)
    return !editDisabled ? !checkedResultByRuleType.find(x => isSameDateTime(x.startDate, startDateTime)) : true
  }, [currentRule, currentSeasonRules, editDisabled, startDateTime])

  const isRuleLengthValid = isRuleNameMinLengthValid(ruleName) && isRuleNameMaxLengthValid(ruleName)
  const isRuleUnique =
    isRuleNameUnique(
      currentSeasonRules,
      ruleName,
      !isRuleVirtual ? currentRule?.id : currentRule.virtualId,
      editDisabled,
    ) ||
    (editDisabled && ruleType === RuleType.promo)
  const isRuleNameUniqueAndValid = isRuleLengthValid && isRuleUnique

  const ruleInvalidToBeSaved =
    !isRuleNameUniqueAndValid ||
    !isStartDateUnique ||
    (editDisabled && !hasRuleSelectedChangesToSave) ||
    (ruleType !== RuleType.season &&
      (isDateBefore(endDateTime, startDateTime) || isSameDateTime(endDateTime, startDateTime))) ||
    (isTypeOfChannelStore && ruleType === RuleType.season)

  useEffect(() => {
    const hasRuleDataChangeOrIsVirtual =
      currentRule &&
      (isRuleVirtual ||
        ruleName !== currentRule.name ||
        ruleType !== currentRule.ruleType ||
        ruleDecimalPrice !== currentRule.ruleDecimalPrice ||
        !isSameDateTime(startDateTime, currentRule.startDate) ||
        !isSameDateTime(endDateTime, currentRule.endDate) ||
        discountToSave.length > 0)
    dispatch(setHasRuleSelectedChangesToSave(hasRuleDataChangeOrIsVirtual))
  }, [
    currentRule,
    discountToSave,
    dispatch,
    endDateTime,
    ruleName,
    ruleType,
    ruleDecimalPrice,
    startDateTime,
    isRuleVirtual,
  ])

  const onNameChange = useCallback(
    e => {
      const newName = e.target.value

      if (isRuleLockedByCurrentUser(locks, ruleId, TypeOfLock.Omnichannel, user)) {
        setRuleName(newName)
      } else {
        dispatch(acquireLock(false)).then(x => {
          if (x) {
            setRuleName(newName)
          }
        })
      }
    },
    [dispatch, locks, ruleId, user],
  )

  const onRuleTypeChange = useCallback(
    value => {
      dispatch(acquireLock(false)).then(x => {
        if (x) {
          setRuleType(value)
        }
      })
    },
    [dispatch],
  )

  const onRuleDecimalChange = useCallback(
    value => {
      dispatch(acquireLock(false)).then(x => {
        if (x) {
          setRuleDecimalPrice(value)
        }
      })
    },
    [dispatch],
  )

  const verifyAndSetStartDate = startDate => {
    dispatch(acquireLock(false)).then(x => {
      if (!x) return
      setStartDateTime(startDate.valueOf() >= dayjs().valueOf() ? startDate : dayjs())
    })
  }

  const verifyAndSetEndDate = endDate => {
    dispatch(acquireLock(false)).then(x => {
      if (!x) return
      const validendDate =
        endDate.valueOf() >= startDateTime.valueOf()
          ? endDate
          : startDateTime.valueOf() >= dayjs().valueOf()
          ? startDateTime.clone().add(1, 'd')
          : dayjs().add(1, 'd')
      setEndDateTime(validendDate)
    })
  }

  const deleteCurrentRule = useCallback(
    e => {
      stopPropagation(e)
      dispatch(deleteRule(currentRule, false, navigationWrapper))
    },
    [dispatch, currentRule],
  )

  const saveCurrentRule = useCallback(() => {
    if (ruleInvalidToBeSaved) {
      return
    }

    const discountRule = { ...currentRule }
    discountRule.name = ruleName
    discountRule.ruleType = ruleType
    discountRule.ruleDecimalPrice = ruleDecimalPrice
    discountRule.startDate = startDateTime.toDate().toISOString()
    discountRule.endDate = endDateTime.toDate().toISOString()
    discountRule.typeOfChannel = typeOfChannel

    dispatch(saveSeasonRule(discountRule, discountToSave, navigationWrapper)).then(newId => {
      if (isTypeOfChannelStore && newId) {
        sendMessage({ type: 'Omnichannel', ruleId: newId })
      }
    })

  }, [
    ruleInvalidToBeSaved,
    currentRule,
    ruleName,
    ruleType,
    startDateTime,
    ruleDecimalPrice,
    endDateTime,
    dispatch,
    discountToSave,
  ])

  const copyRule = useCallback(
    e => {
      e.stopPropagation()
      if (!currentRule || currentRule.virtualId || hasRuleSelectedChangesToSave) {
        return
      }

      const verifiedType =
        isTypeOfChannelStore && ruleType === RuleType.season ? RuleType.salesPeriod : ruleType

      const ruleToCopy = generateRuleCopy(language, ruleName, currentSeasonRules, verifiedType, currentRule)

      if (ruleToCopy.name.length > maxRuleLength) {
        return notification.open({
          description: renderTranslation(language, TranslationKeys.warningSeasonTextCharLength),
          placement: 'topLeft',
          message: '',
        })
      }

      // add new season rule to existing season
      dispatch(saveSeasonRuleCopy(ruleToCopy, currentRule.id, navigationWrapper))
    },
    [
      currentRule,
      currentSeasonRules,
      dispatch,
      hasRuleSelectedChangesToSave,
      isTypeOfChannelStore,
      language,
      navigationWrapper,
      ruleName,
      ruleType,
    ],
  )

  const listingData = useMemo(() => {
    const mappedListing = currentActiveListings.map(listing => {
      return {
        title: listing.name,
        value: listing.name,
        key: listing.name,
      }
    })

    /*if (pricelists.indexOf(VariantModelValue) === -1) {
      mappedListing.unshift({ title: VariantModelValue, value: VariantModelValue, key: VariantModelValue })
    }*/

    mappedListing.unshift({
      title: renderTranslation(language, TranslationKeys.channelListOptionAllText),
      value: listingsAllValue,
      key: listingsAllValue,
    })

    return mappedListing
  }, [currentActiveListings, typeOfChannel, language])

  const onListingSelect = useCallback(
    listingValues => {
      if (
        (listingValues.find(x => x.value === listingsAllValue) &&
          !currentListing.find(x => x.value === listingsAllValue)) ||
        listingValues.length === 0
      ) {
        setListings([listingDefaultValue])
        return
      }

      const listingToSave = listingValues
        .filter(x => x.value !== listingsAllValue)
        .map(x => {
          return {
            label: x.label,
            value: x.value,
          }
        })

      setListings(listingToSave)
    },
    [currentListing, listingDefaultValue],
  )

  const disabledPickerDate = useCallback(current => {
    // Can not select days before today and hour before current time
    return current && current < dayjs().startOf('day')
  }, [])

  const disabledPickerTime = useCallback(
    current => ({
      disabledHours: () => getHoursBeforeCurrentHour(current),
      disabledMinutes: selectedHour => getMinutesBeforeCurrentMinutesForCurrentHour(selectedHour, current),
    }),
    [],
  )

  const modalTriggerClickHandler = useCallback(
    event => {
      const callback = () => {
        if (cleaningPopup) return
        event.preventDefault()
        event.stopPropagation()
        dispatch(toggleSeasonConfirmChangeModal(true))
      }

      const extraRef = document.querySelector('.collapse-container')

      handleModalTriggerEvent(event, bodyrefContext?.current, hasRuleSelectedChangesToSave, callback, null, extraRef)
    },
    [bodyrefContext, hasRuleSelectedChangesToSave, cleaningPopup, dispatch],
  )

  useEffect(() => {
    document.addEventListener('click', modalTriggerClickHandler, true)
    return () => {
      document.removeEventListener('click', modalTriggerClickHandler, true)
    }
  }, [modalTriggerClickHandler])

  const isRuleLocked = useMemo(
    () => isRuleLockedByOtherUsers(locks, ruleId, TypeOfLock.Omnichannel, user),
    [locks, ruleId, user],
  )

  useEffect(() => {
    dispatch(getAnswers(SettingsName.Application))
  }, [])

  useEffect(() => {
    const values = answers?.Application?.questionGroup?.questions.find(
      item => item.code === QuestionCode.OMNI_DECIMAL_ROUNDING,
    )?.possibleValues
    setDecimalPriceList(
      values
        ? [
            { label: renderTranslation(language, TranslationKeys.emptyOnSingleanswer), value: '0' },
            ...Object.entries(values).map(([key]) => ({ label: key, value: key })),
          ]
        : [{ label: renderTranslation(language, TranslationKeys.emptyOnSingleanswer), value: '0' }],
    )
  }, [answers])

  const onGenerateLabels = () => {
    setIsGeneratingLabels(true)
    dispatch(generateLabels(ruleId))
      .then(() => {
        notification.open({
          description: renderTranslation(language, TranslationKeys.okGenerateLabelButton),
          placement: 'topRight',
          message: '',
        })
      })
      .finally(() => setIsGeneratingLabels(false))
  }

  return (
    <div className='w-100'>
      {currentRule ? (
        <div className='omni-season-rule-box'>
          <ConfirmSeasonRuleChangeModal
            onChangeConfirm={saveCurrentRule}
            onChangeUndo={() => initMapRuleData(currentRule)}
            bodyref={bodyrefContext}
          />
          <div className='title-box'>
            <div className='rule-box-title'>{renderTranslation(language, TranslationKeys.ruleBoxSetSeasonRule)}</div>

            <Popover
              content={renderTranslation(language, TranslationKeys.ruleBoxPopoverCopyRule)}
              trigger='hover'
              placement='bottom'
              getPopupContainer={getHtmlParentElement}
            >
              <CopyOutlined
                className={
                  'generic-icon icon-rule-box' +
                  (!currentRule || currentRule.virtualId || hasRuleSelectedChangesToSave ? ' disable' : '')
                }
                rotate={180}
                onClick={copyRule}
              />
            </Popover>
            {(editDisabled && ruleType !== RuleType.promo) || isRuleLocked ? null : (
              <Popover
                content={renderTranslation(language, TranslationKeys.ruleBoxPopoverSaveRule)}
                trigger='hover'
                placement='bottom'
                getPopupContainer={getHtmlParentElement}
              >
                <SaveFilled
                  className={
                    'generic-icon icon-rule-box save-icon save-rule-icon' +
                    ((editDisabled && ruleType !== RuleType.promo) || ruleInvalidToBeSaved
                      ? ' disable'
                      : hasRuleSelectedChangesToSave
                      ? ' active'
                      : '')
                  }
                  onClick={saveCurrentRule}
                />
              </Popover>
            )}
            {isRuleLocked ? (
              <div className='rule-box-editing'>{renderTranslation(language, TranslationKeys.ruleBoxEditing)}</div>
            ) : null}
            {editDisabled ? null : (
              <Popover
                content={renderTranslation(language, TranslationKeys.ruleBoxPopoverDeleteRule)}
                trigger='hover'
                placement='left'
              >
                <Popconfirm
                  key={'popconfirm-delete-active-rule'}
                  title={renderTranslation(language, TranslationKeys.deleteSingleRulePopoverText)}
                  onConfirm={deleteCurrentRule}
                  onCancel={stopPropagation}
                  okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
                  cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
                  disabled={isRuleVirtual ? false : editDisabled}
                  placement='bottomRight'
                  getPopupContainer={getHtmlParentElement}
                >
                  <DeleteFilled
                    className={'generic-icon icon-rule-box ' + (editDisabled ? ' disable' : '')}
                    onClick={stopPropagation}
                  />
                </Popconfirm>
              </Popover>
            )}
          </div>
          <div className='omni-season-rule-form'>
            <Row className='gap-8 justify-space-between' align='middle'>
              <Col xxl={17} xl={15} lg={24}>
                <Row className='gap-8' wrap={ruleType == RuleType.season}>
                  <Col span={8}>
                    <div className='upper-label'>
                      {renderTranslation(language, TranslationKeys.ruleBoxRuleName)}
                      {
                        <span className='lower-label input-warning ml-2'>
                          {!editDisabled && !isRuleLengthValid
                            ? renderTranslation(language, TranslationKeys.warningSeasonTextCharLength)
                            : !editDisabled && !isRuleUnique
                            ? renderTranslation(language, TranslationKeys.warningSeasonNameAlreadyTaken)
                            : null}
                        </span>
                      }
                    </div>
                    <Input
                      placeholder={renderTranslation(language, TranslationKeys.ruleBoxRuleNamePlaceholder)}
                      status={
                        isRuleLocked ? 'warning' : !editDisabled && !isRuleNameUniqueAndValid ? 'error' : undefined
                      }
                      maxLength={maxRuleLength}
                      value={ruleName}
                      onChange={onNameChange}
                      allowClear={{
                        clearIcon: <CloseOutlined className='general-icon input-clear-icon' />,
                      }}
                      disabled={editDisabled}
                    />
                  </Col>
                  <Col className='decimal-price-section'>
                    <div
                      className='upper-label'
                      title={renderTranslation(language, TranslationKeys.ruleBoxDecimalPricePlaceholder)}
                    >
                      {renderTranslation(language, TranslationKeys.ruleBoxDecimalPricePlaceholder)}
                    </div>
                    <Select
                      ref={ruleDecimalDropdownRef}
                      value={ruleDecimalPrice}
                      onChange={onRuleDecimalChange}
                      className='section-dropdown'
                      disabled={editDisabled}
                      options={decimalPriceList}
                      status={isRuleLocked ? 'warning' : undefined}
                    />
                  </Col>
                  <Col className='rule-type-section'>
                    <div className='upper-label'>&nbsp;</div>
                    <Select
                      ref={ruleTypeDropdownRef}
                      value={ruleType}
                      onChange={onRuleTypeChange}
                      className='section-dropdown'
                      disabled={editDisabled}
                      getPopupContainer={getHtmlParentElement}
                      options={ruleOptionsType}
                      status={isRuleLocked ? 'warning' : undefined}
                    />
                  </Col>
                  <Col className='start-date-section'>
                    <Popover
                      content={renderTranslation(language, TranslationKeys.dateTimezoneLabel)}
                      trigger='hover'
                      getPopupContainer={getHtmlParentElement}
                    >
                      <div className='upper-label'>
                        {renderTranslation(language, TranslationKeys.ruleBoxStartDate)}
                        {isStartDateUnique ? null : (
                          <span className='lower-label input-warning ml-2'>
                            {renderTranslation(language, TranslationKeys.warningSeasonDateAlreadyUsed)}
                          </span>
                        )}
                      </div>
                    </Popover>
                    <DateTimeRangeFilter
                      className=''
                      inColumn={false}
                      onDateFromChange={verifyAndSetStartDate}
                      hideTo={true}
                      fullFormat={true}
                      setDateFrom={startDateTime}
                      setStatusFrom={
                        isRuleLocked ? 'warning' : !editDisabled && !isStartDateUnique ? 'error' : undefined
                      }
                      setDisableDateFrom={disabledPickerDate}
                      setDisableTimeFrom={disabledPickerTime}
                      disableFrom={editDisabled}
                      setShowTime={{
                        minuteStep: 15,
                      }}
                      setOnOkFrom={verifyAndSetStartDate}
                      setModalFix={true}
                    />
                  </Col>
                  {ruleType !== RuleType.season ? (
                    <Col className='end-date-section'>
                      <Popover
                        content={renderTranslation(language, TranslationKeys.dateTimezoneLabel)}
                        trigger='hover'
                        getPopupContainer={getHtmlParentElement}
                      >
                        <div className='upper-label'>{renderTranslation(language, TranslationKeys.ruleBoxEndDate)}</div>
                      </Popover>
                      <DateTimeRangeFilter
                        className=''
                        inColumn={false}
                        onDateToChange={verifyAndSetEndDate}
                        hideFrom={true}
                        fullFormat={true}
                        setDateTo={endDateTime}
                        setStatusTo={
                          isRuleLocked
                            ? 'warning'
                            : isDateBefore(endDateTime, startDateTime) || isSameDateTime(endDateTime, startDateTime)
                            ? 'error'
                            : undefined
                        }
                        setDisableDateTo={disabledPickerDate}
                        setDisableTimeTo={disabledPickerTime}
                        disableTo={editDisabled}
                        setShowTime={{
                          minuteStep: 15,
                        }}
                        setModalFix={true}
                        setOnOkTo={verifyAndSetEndDate}
                      />
                    </Col>
                  ) : null}
                </Row>
              </Col>
              <Col xxl={6} xl={8} lg={24}>
                <Row className='pricelist-filter-omni'>
                  <Col>
                    <div className='upper-label'>
                      {renderTranslation(language, TranslationKeys.ruleBoxChannelSelection)}
                    </div>
                    <TreeSelect
                      ref={listingDropdownRef}
                      className='pricelist-selector'
                      style={{ width: 'auto', maxWidth: '400px' }}
                      treeData={listingData}
                      treeCheckable={true}
                      treeCheckStrictly={true}
                      onChange={onListingSelect}
                      value={currentListing}
                      getPopupContainer={getHtmlParentElement}
                    />{' '}
                    {typeOfChannel === TypeOfChannel.Store ? (
                      <>
                        <Button disabled={ruleId < 0} onClick={onGenerateLabels} loading={isGeneratingLabels}>
                          {renderTranslation(language, TranslationKeys.generateLabelButton)}
                          <LabelsIcon />
                        </Button>{' '}
                      </>
                    ) : null}
                    <ExcelButton
                      filename='Omnichannel'
                      RowHead={{
                        RuleName: ruleName,
                        Season: seasonName,
                        DateStart: dayjs(startDateTime).format(`${DateFormat} ${TimeFormat}`),
                        DateEnd: dayjs(endDateTime).format(`${DateFormat} ${TimeFormat}`),
                        DecimalRound: ruleDecimalPrice,
                        RuleType: ruleType,
                      }}
                      RuleId={ruleId}
                      subrule={false}
                      editDisabled={editDisabled}
                      typeOfChannel={typeOfChannel}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </div>
      ) : null}
      <DiscountTable currentListing={currentListing} />
    </div>
  )
}

export default RuleDetail
